/**
 * Post controller: the logic behind the frozen `PostPipeline` API (tier selection, tuning, feedback timing),
 * driving a `PostTarget` (the real GPU graph, or a fake in tests). `render()` and `setFeedback()` are
 * allocation-free (D-014); `setPreset()` only flips a pipeline and reconfigures the bloom chain.
 */
import type { QualityPreset, TuningGroup, TuningParam } from '../contracts'
import { BLOOM_LEVELS, type PostFile, type PostTier } from './data'
import { createFeedbackState, setFeedbackInput, stepFeedback, type FeedbackConfig } from './feedback'
import type { PostDebugView, PostTarget } from './graph'
import type { PostPipeline } from './index'
import { bloomLevelWeights } from './tiers'

/** Milliseconds clock (performance.now in the browser; a fake in tests). */
export type Clock = () => number

type TierTable = Record<QualityPreset, PostTier>

function num(p: TuningParam): number {
  return typeof p.value === 'number' ? p.value : Number(p.value)
}

export interface PostController extends PostPipeline {
  /** Current preset (tests and lookdev). */
  readonly preset: QualityPreset
  /** Lookdev pass diagnostics: renders one buffer alone. */
  renderDebug(view: PostDebugView): void
  /** Tier values after tuning (tests). */
  readonly tiers: Readonly<TierTable>
}

export function createPostController(target: PostTarget, data: PostFile, initial: QualityPreset, clock: Clock): PostController {
  const u = target.uniforms
  // Mutable copies: tuning edits these, never the parsed file.
  const tiers: TierTable = {
    high: { ...data.tiers.high, ao: { ...data.tiers.high.ao }, godRays: { ...data.tiers.high.godRays } },
    medium: { ...data.tiers.medium, ao: { ...data.tiers.medium.ao }, godRays: { ...data.tiers.medium.godRays } },
    low: { ...data.tiers.low, ao: { ...data.tiers.low.ao }, godRays: { ...data.tiers.low.godRays } },
  }
  const bloomCfg = { ...data.bloom, levelFactors: [...data.bloom.levelFactors] }
  const fbCfg: FeedbackConfig = { ...data.feedback }
  const aoCfg = { ...data.ao }
  const fb = createFeedbackState()
  const weights = new Float32Array(BLOOM_LEVELS)
  let preset = initial
  let tier = tiers[preset]
  // Frame time in a typed array: a double stored in a closure variable would box a new heap number per frame.
  const time = new Float64Array(1)
  time[0] = clock()

  const params: Record<string, TuningParam> = {
    threshold: { value: bloomCfg.threshold, min: 0, max: 4, step: 0.01 },
    smoothWidth: { value: bloomCfg.smoothWidth, min: 0, max: 1, step: 0.01 },
    strength: { value: bloomCfg.strength, min: 0, max: 4, step: 0.01 },
    radius: { value: tier.radius, min: 0, max: 1, step: 0.01 },
    upsampleSpread: { value: bloomCfg.upsampleSpread, min: 0.25, max: 3, step: 0.05 },
    vignette: { value: tier.vignette, min: 0, max: 1, step: 0.01 },
    dirt: { value: tier.dirt, min: 0, max: 1, step: 0.01 },
    edgeGain: { value: fbCfg.edgeGain, min: 0, max: 1, step: 0.01 },
    caStrength: { value: fbCfg.caStrength, min: 0, max: 0.03, step: 0.0005 },
    boostSaturation: { value: fbCfg.boostSaturation, min: 0, max: 1, step: 0.01 },
    boostExposure: { value: fbCfg.boostExposure, min: 0, max: 1, step: 0.01 },
    boostVignette: { value: fbCfg.boostVignette, min: 0, max: 1, step: 0.01 },
    dither: { value: data.dither, min: 0, max: 4, step: 0.1 },
    grain: { value: tier.grain, min: 0, max: 0.1, step: 0.001 },
    caEdge: { value: tier.caEdge, min: 0, max: 0.004, step: 0.0001 },
    lut: { value: data.grade.intensity, min: 0, max: 1, step: 0.01 },
    aoRadius: { value: aoCfg.radius, min: 0.1, max: 8, step: 0.05 },
    aoStrength: { value: aoCfg.strength, min: 0, max: 1, step: 0.01 },
    aoThickness: { value: aoCfg.thickness, min: 0.1, max: 8, step: 0.05 },
  }

  function applyTier(): void {
    tier = tiers[preset]
    u.dirt.value = tier.bloom === 'off' ? 0 : tier.dirt
    u.vignette.value = tier.vignette
    u.grain.value = tier.grain
    u.caEdge.value = tier.caEdge
    if (tier.bloom !== 'off') {
      const n = bloomLevelWeights(bloomCfg.levelFactors, tier.radius, tier.bloom, weights)
      target.configureBloom(tier.bloom, weights, n)
    }
    params.radius.value = tier.radius
    params.vignette.value = tier.vignette
    params.dirt.value = tier.dirt
    params.grain.value = tier.grain
    params.caEdge.value = tier.caEdge
    target.configureAtmosphere({ godRaySamples: tier.godRays.samples, godRayScale: tier.godRays.scale })
    target.configureAo({
      samples: tier.ao.samples,
      scale: tier.ao.scale,
      maxPixels: tier.ao.maxPixels,
      upsampleDepth: aoCfg.upsampleDepth,
      upsampleNormal: aoCfg.upsampleNormal,
      radius: aoCfg.radius,
      thickness: aoCfg.thickness,
      strength: aoCfg.strength,
      power: aoCfg.power,
      fade: [aoCfg.fadeStart, aoCfg.fadeEnd],
    })
  }

  function applyGlobals(): void {
    u.threshold.value = bloomCfg.threshold
    u.smoothWidth.value = bloomCfg.smoothWidth
    u.strength.value = bloomCfg.strength
    u.spread.value = bloomCfg.upsampleSpread
    u.boostSaturation.value = fbCfg.boostSaturation
    u.boostExposure.value = fbCfg.boostExposure
    u.boostVignette.value = fbCfg.boostVignette
  }

  const tuning: TuningGroup = {
    name: 'Post',
    params,
    apply(): void {
      bloomCfg.threshold = num(params.threshold)
      bloomCfg.smoothWidth = num(params.smoothWidth)
      bloomCfg.strength = num(params.strength)
      bloomCfg.upsampleSpread = num(params.upsampleSpread)
      // Tier values edit the active tier; switching presets shows the other tier's values.
      tier.radius = num(params.radius)
      tier.vignette = num(params.vignette)
      tier.dirt = tier.bloom === 'off' ? 0 : num(params.dirt)
      tier.grain = num(params.grain)
      tier.caEdge = num(params.caEdge)
      u.lut.value = num(params.lut)
      fbCfg.edgeGain = num(params.edgeGain)
      fbCfg.caStrength = num(params.caStrength)
      fbCfg.boostSaturation = num(params.boostSaturation)
      fbCfg.boostExposure = num(params.boostExposure)
      fbCfg.boostVignette = num(params.boostVignette)
      u.dither.value = num(params.dither)
      aoCfg.radius = num(params.aoRadius)
      aoCfg.strength = num(params.aoStrength)
      aoCfg.thickness = num(params.aoThickness)
      applyGlobals()
      applyTier()
    },
  }

  applyGlobals()
  applyTier()

  const ctl: PostController = {
    tuning,
    get preset(): QualityPreset {
      return preset
    },
    tiers,
    render(): void {
      const now = clock()
      stepFeedback(fb, fbCfg, (now - time[0]) / 1000)
      time[0] = now
      const v = u.frame.value
      // Film grain seed: a frame counter (wraps; lookdev renders stay deterministic per run).
      v.w = (v.w + 1) % 4096
      v.x = fb.edge * fbCfg.edgeGain
      v.y = fb.ca * fbCfg.caStrength
      v.z = fb.boost
      if (tier.bloom === 'off') target.renderPlain()
      else target.renderBloom()
    },
    renderDebug(view: PostDebugView): void {
      target.renderDebug(view)
    },
    setFeedback(damage: number, boost: number): void {
      setFeedbackInput(fb, damage, boost)
    },
    setSize(width: number, height: number, pixelRatio: number): void {
      target.setSize(width, height, pixelRatio)
    },
    setPreset(p: QualityPreset): void {
      if (p === preset) return
      preset = p
      applyTier()
    },
    dispose(): void {
      target.dispose()
    },
  }
  return ctl
}
